import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  class: "flex flex-col gap-4 text-left",
  "data-testid": "pin-step-3"
}
const _hoisted_2 = { class: "h-48 w-full overflow-hidden rounded-xl border border-gray-100" }
const _hoisted_3 = { class: "divide-y divide-gray-100 rounded-xl border border-gray-100 bg-white text-sm" }
const _hoisted_4 = {
  class: "flex flex-wrap gap-2 px-4 py-2.5",
  "data-testid": "pin-summary-unit"
}
const _hoisted_5 = /*#__PURE__*/_createElementVNode("dt", { class: "w-40 shrink-0 text-gray-500" }, "Unidade", -1)
const _hoisted_6 = { class: "flex-1 text-gray-800" }
const _hoisted_7 = {
  class: "flex flex-wrap gap-2 px-4 py-2.5",
  "data-testid": "pin-summary-origin"
}
const _hoisted_8 = /*#__PURE__*/_createElementVNode("dt", { class: "w-40 shrink-0 text-gray-500" }, "Origem do ponto", -1)
const _hoisted_9 = { class: "flex-1 text-gray-800" }
const _hoisted_10 = {
  class: "flex flex-wrap gap-2 px-4 py-2.5",
  "data-testid": "pin-summary-coordinates"
}
const _hoisted_11 = /*#__PURE__*/_createElementVNode("dt", { class: "w-40 shrink-0 text-gray-500" }, "Coordenadas", -1)
const _hoisted_12 = { class: "flex-1 font-mono text-gray-800" }
const _hoisted_13 = {
  key: 0,
  class: "flex flex-wrap gap-2 px-4 py-2.5",
  "data-testid": "pin-summary-moved"
}
const _hoisted_14 = /*#__PURE__*/_createElementVNode("dt", { class: "w-40 shrink-0 text-gray-500" }, "Pino movido", -1)
const _hoisted_15 = { class: "flex-1 text-gray-800" }
const _hoisted_16 = {
  class: "flex flex-wrap gap-2 px-4 py-2.5",
  "data-testid": "pin-summary-radius"
}
const _hoisted_17 = /*#__PURE__*/_createElementVNode("dt", { class: "w-40 shrink-0 text-gray-500" }, "Raio", -1)
const _hoisted_18 = { class: "flex-1 text-gray-800" }
const _hoisted_19 = {
  key: 0,
  class: "text-gray-500"
}
const _hoisted_20 = {
  key: 1,
  class: "flex flex-wrap gap-2 px-4 py-2.5",
  "data-testid": "pin-summary-checks"
}
const _hoisted_21 = /*#__PURE__*/_createElementVNode("dt", { class: "w-40 shrink-0 text-gray-500" }, "Checks afetados (14 dias)", -1)
const _hoisted_22 = { class: "flex-1 text-gray-800" }
const _hoisted_23 = /*#__PURE__*/_createElementVNode("span", { class: "text-sm text-gray-700" }, " Confirmo pela vista de satélite que o marcador está sobre o prédio onde os médicos realizam o check. ", -1)
const _hoisted_24 = { class: "w-full sm:max-w-[360px]" }
const _hoisted_25 = { class: "rounded-xl bg-[#EEF1F3] p-3 text-xs text-gray-600" }
const _hoisted_26 = /*#__PURE__*/_createElementVNode("p", null, " Fica registrado: quem alterou, de onde para onde, raio antes e depois, motivo e origem do ponto. ", -1)
const _hoisted_27 = /*#__PURE__*/_createElementVNode("p", { class: "mt-1" }, "A alteração vale para os próximos check-ins; os anteriores não mudam.", -1)
const _hoisted_28 = {
  key: 0,
  class: "mt-1"
}

import { computed } from 'vue'
import GoogleMaps from '@/components/GoogleMaps.vue'
import GenericInput from '@/components/designSystem/Inputs/GenericInput.vue'
import {
  PIN_CHANGE_REASON_LABELS,
  type PinChangeReason
} from '@/Network/Types/Responses/PinSuggestionResponses'
import { formatMeters } from '@/utils/geo'
import { unitTypeLabel } from '../../unitType'
import type { UnitGeolocationApi } from '../../composables/useUnitGeolocation'
import type { PinModalMode } from './pinModalTypes'

type DoctorSummary = { total: number; rejected: number; doctors: number; days: number } | null


export default /*#__PURE__*/_defineComponent({
  __name: 'StepConfirm',
  props: {
    geo: {},
    unitName: {},
    mapType: {},
    doctorSummary: {},
    mode: {}
  },
  setup(__props: any) {

const props = __props;



/**
 * Os campos editáveis do passo 3 vivem no estado compartilhado do composable.
 * Desestruturar os refs deixa o template escrever neles sem mutar a prop `geo`.
 */
const { confirmedOnBuilding, reason, reasonDetail, radiusJustification } = props.geo

const reasonOptions = (Object.keys(PIN_CHANGE_REASON_LABELS) as PinChangeReason[]).map((value) => ({
  title: PIN_CHANGE_REASON_LABELS[value],
  value
}))

const point = computed(() => props.geo.currentPoint.value)

const coordinates = computed(() =>
  point.value ? `${point.value.lat.toFixed(6)}, ${point.value.lng.toFixed(6)}` : '—'
)

const isOtherReason = computed(() => props.geo.reason.value === 'OTHER')

/** Traduz a origem para o que o usuário fez, não para o enum do backend. */
const originLabel = computed(() => {
  const confidence =
    props.geo.confidence.value === 'BUILDING' ? 'Prédio identificado' : 'Endereço aproximado'

  switch (props.geo.origin.value) {
    case 'PLACES':
      return `Google Places · ${confidence}`
    case 'ADDRESS':
      return 'Endereço do cadastro'
    case 'LINK':
      return 'Link do Google Maps'
    case 'SUGGESTION':
      return 'Sugestão dos checks'
    case 'MANUAL':
      return 'Ajuste manual no mapa'
    default:
      return '—'
  }
})

return (_ctx: any,_cache: any) => {
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_v_select = _resolveComponent("v-select")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (point.value)
        ? (_openBlock(), _createBlock(GoogleMaps, {
            key: 0,
            latitude: point.value.lat,
            longitude: point.value.lng,
            radius: _ctx.geo.reachRadius.value != null ? Number(_ctx.geo.reachRadius.value) : undefined,
            address: _ctx.geo.formattedAddress.value,
            draggable: false,
            "map-type": _ctx.mapType,
            "show-info": false
          }, null, 8, ["latitude", "longitude", "radius", "address", "map-type"]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("dl", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _hoisted_5,
        _createElementVNode("dd", _hoisted_6, _toDisplayString(_ctx.unitName || '—') + " · " + _toDisplayString(_unref(unitTypeLabel)(_ctx.geo.unitType.value)), 1)
      ]),
      _createElementVNode("div", _hoisted_7, [
        _hoisted_8,
        _createElementVNode("dd", _hoisted_9, _toDisplayString(originLabel.value), 1)
      ]),
      _createElementVNode("div", _hoisted_10, [
        _hoisted_11,
        _createElementVNode("dd", _hoisted_12, _toDisplayString(coordinates.value), 1)
      ]),
      ((_ctx.geo.movedDistanceM.value ?? 0) > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
            _hoisted_14,
            _createElementVNode("dd", _hoisted_15, _toDisplayString(_unref(formatMeters)(_ctx.geo.movedDistanceM.value)) + " para " + _toDisplayString(_ctx.geo.movedBearing.value), 1)
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_16, [
        _hoisted_17,
        _createElementVNode("dd", _hoisted_18, [
          _createTextVNode(_toDisplayString(_ctx.geo.reachRadius.value) + " m ", 1),
          (_ctx.geo.originalRadius.value != null)
            ? (_openBlock(), _createElementBlock("span", _hoisted_19, " (antes: " + _toDisplayString(_ctx.geo.originalRadius.value.toLocaleString('pt-BR')) + " m) ", 1))
            : _createCommentVNode("", true)
        ])
      ]),
      (_ctx.doctorSummary)
        ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
            _hoisted_21,
            _createElementVNode("dd", _hoisted_22, _toDisplayString(_ctx.doctorSummary.total - _ctx.doctorSummary.rejected) + " aceitos · " + _toDisplayString(_ctx.doctorSummary.rejected) + " rejeitados · " + _toDisplayString(_ctx.doctorSummary.doctors) + " " + _toDisplayString(_ctx.doctorSummary.doctors === 1 ? 'médico' : 'médicos'), 1)
          ]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(_component_v_checkbox, {
      class: "-ml-1",
      color: "primary",
      density: "comfortable",
      "hide-details": "",
      "model-value": _unref(confirmedOnBuilding),
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (confirmedOnBuilding.value = !!$event)),
      "data-testid": "pin-confirm-checkbox"
    }, {
      label: _withCtx(() => [
        _hoisted_23
      ]),
      _: 1
    }, 8, ["model-value"]),
    (_ctx.geo.needsReason.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _createElementVNode("div", _hoisted_24, [
            _createVNode(_component_v_select, {
              label: "* Motivo da alteração",
              variant: "outlined",
              density: "comfortable",
              "hide-details": "auto",
              items: _unref(reasonOptions),
              "model-value": _unref(reason),
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (reason.value = $event)),
              "data-testid": "pin-reason-select"
            }, null, 8, ["items", "model-value"])
          ]),
          _createVNode(GenericInput, {
            label: isOtherReason.value ? '* Detalhe' : 'Detalhe (opcional)',
            "model-value": _unref(reasonDetail),
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (reasonDetail.value = $event ?? '')),
            width: "100%",
            "data-testid": "pin-reason-detail-input"
          }, null, 8, ["label", "model-value"])
        ], 64))
      : _createCommentVNode("", true),
    (_ctx.geo.isRadiusOffPreset.value)
      ? (_openBlock(), _createBlock(GenericInput, {
          key: 1,
          label: "* Justificativa do raio fora do preset",
          "model-value": _unref(radiusJustification),
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (radiusJustification.value = $event ?? '')),
          width: "100%",
          hint: `Preset do tipo: ${_ctx.geo.radiusPreset.value} m`,
          "persistent-hint": "",
          "data-testid": "pin-radius-justification-input"
        }, null, 8, ["model-value", "hint"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_25, [
      _hoisted_26,
      _hoisted_27,
      (_ctx.mode === 'form')
        ? (_openBlock(), _createElementBlock("p", _hoisted_28, " O pino é gravado junto com o cadastro da unidade, ao salvar o formulário. "))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})